import { DataProvider } from '../../helpers/data-provider'
import { CommonHelper } from '../../helpers/common-helper'
import { TypeHelper } from '../../helpers/type-helper'

import { Enums } from '../common/enums'
import { OpActorData } from '../custom/op-actor-data'

import { IReferenceType, IOpChangeState, IOpObject, IOpData, ICopyable } from '../util/interfaces'
import { OpDataList } from '../util/op-data-list'
import { ObjectCustomString } from '../util/object-custom-string'
import { Utils } from '../util/utils'
import { OpActorGroup } from './op-actor-group'
import { OpLocation } from './op-location'



export class OpActor implements IReferenceType, IOpObject, IOpChangeState, ICopyable<OpActor> {

    //#region Properties

    public idActor: number
    public name: string
    public surname: string
    public city: string
    public address: string
    public postcode: string
    public email: string
    public mobile: string
    public phone: string
    public idLanguage: number
    public description: string
    public avatar: string

    //#endregion


    //CustomProperties

    public currentGroups: OpActorGroup[] = []
    public currentLocations: OpLocation[] = []

    //#endregion


    constructor(opObj: any) {
        if (opObj instanceof OpActor) {
            this.copy(<OpActor>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdActor
    }

    public getReferenceKey(): number {
        return this.idActor
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idActor
    }
    public set idObject(idObject: any) {
        this.idActor = idObject
    }

    public dataList: OpDataList<OpActorData> = new OpDataList<OpActorData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string;


    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idActor = opObj.ID_ACTOR
            this.name = opObj.NAME
            this.surname = opObj.SURNAME
            this.city = opObj.CITY
            this.address = opObj.ADDRESS
            this.postcode = opObj.POSTCODE
            this.email = opObj.EMAIL
            this.mobile = opObj.MOBILE
            this.phone = opObj.PHONE
            this.idLanguage = opObj.ID_LANGUAGE
            this.description = opObj.DESCRIPTION

            this.dynamicValues = opObj.dynamicValues
            this.opState = opObj.OpState

            if (opObj.AVATAR) {
                if (typeof opObj.AVATAR === 'string') {
                    this.avatar = opObj.AVATAR
                }
                else {
                    this.avatar = CommonHelper.base64ToImage(CommonHelper.bin2String(opObj.AVATAR)) //btoa(new Uint8Array(opObj.AVATAR).reduce((data, byte) => data + String.fromCharCode(byte), ''))
                }
            }

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpActorData(data)))
            }

            if (opObj.CurrentGroups) {
                this.currentGroups.push(...opObj.CurrentGroups.map(groupData => new OpActorGroup(groupData)));
            }

            if (opObj.CurrentLocations) {
                this.currentLocations.push(...opObj.CurrentLocations.map(locationData => new OpLocation(locationData)));
            }
        } else {
            this.idActor = 0
            this.opState = Enums.OpChangeState.New
        }

    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_ACTOR: this.idActor,
            NAME: this.name,
            SURNAME: this.surname,
            CITY: this.city,
            ADDRESS: this.address,
            POSTCODE: this.postcode,
            EMAIL: this.email,
            MOBILE: this.mobile,
            PHONE: this.phone,
            ID_LANGUAGE: this.idLanguage,
            DESCRIPTION: this.description,
            AVATAR: this.avatar ? this.avatar : null,

            DataList: this.dataList ? this.dataList.ToJSON(true) : null,
            OpState: this.opState,
            CurrentGroups: this.currentGroups.map(currentGroup => currentGroup.toJSON()),
            CurrentLocations: this.currentLocations.map(currentGroup => currentGroup.toJSON()),

        }
    }

    //#endregion


    //#region toString

    public toString() {
        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (customFormatString == null) {
            return this.surname + ' ' + this.name
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    //#endregion


    //#region ICopyable Implementation

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpActor {
        const newActor = new OpActor(null)

        newActor.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newActor
    }

    public copy(source: OpActor, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return
        }

        if (includeNavigationProperties) { }

        if (includeCustomProperties) {
            if (source.currentGroups != null) {
                this.currentGroups = source.currentGroups.map((actorGroup: OpActorGroup) => actorGroup.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }

            if (source.currentLocations != null) {
                this.currentLocations = TypeHelper.clone(source.currentLocations) // TODO: Copy methods for those types
            }
        }

        if (includeDataList) {
            this.dataList = source.dataList.clone()
        }

        if (includeDynamicValues) {
            if (source.dynamicValues != null) {
                this.dynamicValues = TypeHelper.clone(source.dynamicValues)
            }
        }

        this.idActor = source.idActor
        this.name = source.name
        this.surname = source.surname
        this.city = source.city
        this.address = source.address
        this.postcode = source.postcode
        this.email = source.email
        this.mobile = source.mobile
        this.phone = source.phone
        this.idLanguage = source.idLanguage
        this.description = source.description
        this.avatar = source.avatar

        this.opState = source.opState
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpActor[]): JQueryPromise<OpActor[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    //#endregion


    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpActor {
        const newObject: OpActor = new OpActor(null)

        newObject.idActor = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpActorData.fromIOpData(data)))

        return newObject
    }

    //#endregion    

}